import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body } from '../op-types';

export function createUserQueries(client: StoxlyClient) {
  return createQueryKeys('users', {
    // ['users', 'profile']
    profile: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetProfile({ signal }),
    },

    // ['users', 'preferences']
    preferences: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetPreferences({ signal }),
    },

    // ['users', 'customBenchmarks']
    customBenchmarks: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetCustomBenchmarks({ signal }),
    },

    // ['users', 'customTimeframes']
    customTimeframes: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetCustomTimeframes({ signal }),
    },

    // ['users', 'onboardingStatus']
    onboardingStatus: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetOnboardingStatus({ signal }),
    },

    // ['users', 'onboardingDraft']
    onboardingDraft: {
      queryKey: null,
      queryFn: ({ signal }) => client.usersGetOnboardingDraft({ signal }),
    },
  });
}

export function createUserMutations(client: StoxlyClient) {
  return {
    updateProfile: {
      mutationFn: (body: Body<'usersUpdateProfile'>) => client.usersUpdateProfile(body),
    },
    updatePreferences: {
      mutationFn: (body: Body<'usersUpdatePreferences'>) => client.usersUpdatePreferences(body),
    },
    createCustomBenchmark: {
      mutationFn: (body: Body<'usersCreateCustomBenchmark'>) =>
        client.usersCreateCustomBenchmark(body),
    },
    deleteCustomBenchmark: {
      mutationFn: (id: string) => client.usersDeleteCustomBenchmark(undefined, { params: { id } }),
    },
    createCustomTimeframe: {
      mutationFn: (body: Body<'usersCreateCustomTimeframe'>) =>
        client.usersCreateCustomTimeframe(body),
    },
    deleteCustomTimeframe: {
      mutationFn: (id: string) => client.usersDeleteCustomTimeframe(undefined, { params: { id } }),
    },
    updateOnboardingProgress: {
      mutationFn: (body: Body<'usersUpdateOnboardingProgress'>) =>
        client.usersUpdateOnboardingProgress(body),
    },
    completeOnboarding: {
      mutationFn: () => client.usersCompleteOnboarding(undefined),
    },
    saveOnboardingDraft: {
      mutationFn: (body: Body<'usersSaveOnboardingDraft'>) => client.usersSaveOnboardingDraft(body),
    },
    clearOnboardingDraft: {
      mutationFn: () => client.usersClearOnboardingDraft(undefined),
    },
    importData: {
      mutationFn: () => client.usersImportData(undefined),
    },
    // The user's answer about a statement label their import could not classify. It books
    // nothing; it feeds the admin unknown-label queue.
    confirmUnknownLabel: {
      mutationFn: ({ id, body }: { id: string; body: Body<'importUnknownLabelsConfirm'> }) =>
        client.importUnknownLabelsConfirm(body, { params: { id } }),
    },
    resetOnboardingSession: {
      mutationFn: (body: Body<'usersResetOnboardingSession'>) =>
        client.usersResetOnboardingSession(body),
    },
    activateDemoMode: {
      mutationFn: () => client.usersActivateDemoMode(undefined),
    },
    exitDemoMode: {
      mutationFn: () => client.usersExitDemoMode(undefined),
    },
    claimBetaDiscount: {
      mutationFn: () => client.usersClaimBetaDiscount(undefined),
    },
    clearAccount: {
      mutationFn: () => client.usersClearAccount(undefined),
    },
    // PERMANENT deletion (all data + the Supabase auth user). The optional body carries the
    // anonymous exit survey; a 409 means an active subscription must be cancelled first.
    deleteAccount: {
      mutationFn: (body: Body<'usersDeleteAccount'>) => client.usersDeleteAccount(body),
    },
  };
}
